/*
==============================================================================
Rcrea - Sello CCS seguro para Shopify
Archivo: assets/rcrea-ccs-sello.js
Versión: Footer Safe

Objetivos:
- No crear variables globales como Swal.
- No depender de clases globales del theme.
- Mover sólo el sello visual al footer; NO mantenerlo sticky/fixed en viewport.
- Mantener modal y errores fuera del footer para evitar conflictos de z-index/overflow.
- Cargar PDF.js sólo al validar el sello.
- Restaurar cualquier window.pdfjsLib preexistente para evitar colisiones.
- Evitar doble inicialización y doble petición en dispositivos táctiles.
==============================================================================
*/

(() => {
  'use strict';

  const ROOT_ID = 'rcrea-ccs-root';
  const FOOTER_SEAL_ID = 'rcrea-ccs-footer-seal';
  const ERROR_TIMEOUT_MS = 9000;
  const REQUEST_TIMEOUT_MS = 25000;

  function findFooter() {
    const selectors = [
      'footer.footer',
      'footer[role="contentinfo"]',
      'footer',
      '#shopify-section-footer',
      '[data-section-type="footer"]',
      '.shopify-section-group-footer-group'
    ];

    for (const selector of selectors) {
      const candidate = document.querySelector(selector);
      if (candidate) return candidate;
    }

    return null;
  }

  function initRcreaCcs() {
    const root = document.getElementById(ROOT_ID);

    if (!root || root.dataset.ccsInitialized === 'true') return;

    root.dataset.ccsInitialized = 'true';

    const footerSeal = root.querySelector(`#${FOOTER_SEAL_ID}`);
    const openButton = root.querySelector('#rcrea-ccs-open');
    const modal = root.querySelector('#rcrea-ccs-modal');
    const closeButton = root.querySelector('#rcrea-ccs-close');
    const downloadButton = root.querySelector('#rcrea-ccs-download');
    const pdfWrap = root.querySelector('#rcrea-ccs-pdf-wrap');
    const canvas = root.querySelector('#rcrea-ccs-pdf-canvas');
    const errorBox = root.querySelector('#rcrea-ccs-error');

    const endpoint = root.dataset.ccsEndpoint;
    const pdfJsSrc = root.dataset.ccsPdfjsSrc;
    const workerSrc = root.dataset.ccsWorkerSrc;

    if (
      !footerSeal ||
      !openButton ||
      !modal ||
      !closeButton ||
      !downloadButton ||
      !pdfWrap ||
      !canvas ||
      !errorBox ||
      !endpoint ||
      !pdfJsSrc ||
      !workerSrc
    ) {
      console.warn('[Rcrea CCS] Configuración incompleta. El sello no se inicializó.');
      return;
    }

    let isLoading = false;
    let pdfBlobUrl = null;
    let errorTimer = null;
    let previousFocus = null;
    let ccsPdfJsPromise = null;

    function mountSealInFooter() {
      const footer = findFooter();

      if (!footer) {
        console.warn('[Rcrea CCS] No se encontró el footer. El sello permanecerá oculto hasta que exista un footer válido.');
        footerSeal.style.display = 'none';
        return;
      }

      const computedFooterStyle = window.getComputedStyle(footer);

      if (computedFooterStyle.position === 'static') {
        footer.classList.add('rcrea-ccs__footer-anchor');
      }

      if (footerSeal.parentElement !== footer) {
        footer.appendChild(footerSeal);
      }

      footerSeal.style.removeProperty('display');
      footerSeal.dataset.rcreaCcsMounted = 'true';
    }

    function setLoading(loading) {
      isLoading = loading;
      root.classList.toggle('is-loading', loading);
      footerSeal.classList.toggle('is-loading', loading);
      openButton.disabled = loading;
      openButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    function showError(message) {
      const safeMessage = message || 'En este momento, la aplicación se encuentra fuera de servicio. Intente más tarde.';

      if (errorTimer) window.clearTimeout(errorTimer);

      errorBox.textContent = `❌ ${safeMessage}`;
      errorBox.hidden = false;

      errorTimer = window.setTimeout(() => {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }, ERROR_TIMEOUT_MS);
    }

    function openModal() {
      previousFocus = document.activeElement;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');

      window.requestAnimationFrame(() => {
        closeButton.focus({ preventScroll: true });
      });
    }

    function closeModal() {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');

      if (previousFocus && typeof previousFocus.focus === 'function') {
        previousFocus.focus({ preventScroll: true });
      }
    }

    function restoreGlobal(name, previousValue, hadOwnProperty) {
      if (hadOwnProperty) {
        window[name] = previousValue;
      } else {
        try {
          delete window[name];
        } catch (_error) {
          window[name] = undefined;
        }
      }
    }

    function loadPdfJsIsolated() {
      if (ccsPdfJsPromise) return ccsPdfJsPromise;

      ccsPdfJsPromise = new Promise((resolve, reject) => {
        const hadPdfJs = Object.prototype.hasOwnProperty.call(window, 'pdfjsLib');
        const previousPdfJs = window.pdfjsLib;

        const script = document.createElement('script');
        script.src = pdfJsSrc;
        script.async = true;
        script.dataset.rcreaCcsPdfjs = 'true';

        script.onload = () => {
          const ccsPdfJs = window.pdfjsLib;

          restoreGlobal('pdfjsLib', previousPdfJs, hadPdfJs);
          script.remove();

          if (!ccsPdfJs || typeof ccsPdfJs.getDocument !== 'function') {
            ccsPdfJsPromise = null;
            reject(new Error('No fue posible inicializar el visor del certificado CCS.'));
            return;
          }

          ccsPdfJs.GlobalWorkerOptions.workerSrc = workerSrc;
          resolve(ccsPdfJs);
        };

        script.onerror = () => {
          restoreGlobal('pdfjsLib', previousPdfJs, hadPdfJs);
          script.remove();
          ccsPdfJsPromise = null;
          reject(new Error('No fue posible cargar el visor del certificado CCS.'));
        };

        document.head.appendChild(script);
      });

      return ccsPdfJsPromise;
    }

    async function fetchCertificate() {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          cache: 'no-store',
          credentials: 'omit',
          headers: {
            Accept: 'application/pdf, application/json;q=0.9, */*;q=0.1'
          },
          signal: controller.signal
        });

        const contentType = (response.headers.get('content-type') || '').toLowerCase();

        if (contentType.includes('application/json')) {
          const data = await response.json();
          const message = data && data.message ? data.message : 'No fue posible validar el sitio en este momento.';
          throw new Error(message);
        }

        if (!response.ok) {
          throw new Error('La aplicación está fuera de servicio. Intente más tarde.');
        }

        return response.blob();
      } catch (error) {
        if (error && error.name === 'AbortError') {
          throw new Error('La validación CCS tardó demasiado en responder. Intente nuevamente.');
        }

        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    async function renderFirstPage(pdfJs, blob) {
      const pdfData = await blob.arrayBuffer();
      const loadingTask = pdfJs.getDocument({ data: pdfData });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);

      const baseViewport = page.getViewport({ scale: 1 });
      const cssTargetWidth = Math.min(700, Math.max(280, window.innerWidth - 72));
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const renderScale = (cssTargetWidth / baseViewport.width) * pixelRatio;
      const viewport = page.getViewport({ scale: renderScale });
      const context = canvas.getContext('2d', { alpha: false });

      if (!context) {
        throw new Error('El navegador no pudo preparar la vista del certificado CCS.');
      }

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.round(viewport.width / pixelRatio)}px`;
      canvas.style.height = `${Math.round(viewport.height / pixelRatio)}px`;

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);

      await page.render({
        canvasContext: context,
        viewport
      }).promise;
    }

    async function validateAndOpen() {
      if (isLoading) return;

      setLoading(true);
      errorBox.hidden = true;

      try {
        const [pdfJs, certificateBlob] = await Promise.all([
          loadPdfJsIsolated(),
          fetchCertificate()
        ]);

        if (pdfBlobUrl) {
          URL.revokeObjectURL(pdfBlobUrl);
        }

        pdfBlobUrl = URL.createObjectURL(certificateBlob);

        await renderFirstPage(pdfJs, certificateBlob);
        openModal();
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        showError(message);
      } finally {
        setLoading(false);
      }
    }

    function downloadCertificate() {
      if (!pdfBlobUrl) return;

      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = 'certificado_ccs.pdf';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    mountSealInFooter();

    openButton.addEventListener('click', validateAndOpen);
    closeButton.addEventListener('click', closeModal);
    downloadButton.addEventListener('click', downloadCertificate);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });

    document.addEventListener('shopify:section:load', () => {
      window.requestAnimationFrame(mountSealInFooter);
    });

    window.addEventListener('pagehide', () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        pdfBlobUrl = null;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRcreaCcs, { once: true });
  } else {
    initRcreaCcs();
  }
})();
